import type {
  EventInput,
  FrontendEvent,
  FrontendEventsClient,
} from '@appcharge/frontend-events-sdk';

import { ESdkEvents, EventParams } from '../contracts';
import { getEnvVariable } from '../utils/get-env-variable';
import { deferIdle } from '../utils/defer';
import { logError } from '../utils/coralogix-rum';
import { toFrontendCheckoutEvent } from '../utils/frontend-events-mapper';

export interface FrontendEventsInitOptions {
  /** Checkout session token — sent as `Authorization: checkout <token>`. Required. */
  sessionToken: string;
  /** Publisher id stamped on every event (must match auth identity). */
  publisherId?: string;
  /** Override events-gateway base URL. Defaults to `REACT_APP_EVENTS_API_URL` → `REACT_APP_API_BASE_URL`. */
  baseUrl?: string;
}

type QueuedEvent = {
  event: ESdkEvents;
  params: Partial<EventParams>;
};

type FrontendEventsSdk = typeof import('@appcharge/frontend-events-sdk');

/** Soft cap so a stuck init cannot grow unbounded. */
const MAX_QUEUED_EVENTS = 32;

/**
 * Sentinel the SDK's `collectStandardFields` stamps when it cannot derive the
 * device from the user agent. We strip it so `device_model` is omitted from
 * the payload rather than sending a meaningless `"unknown"`.
 */
const UNKNOWN_DEVICE_MODEL = 'unknown';

/**
 * Checkout UI schema version stamped onto every FE event. Distinguishes this
 * UI (independent-checkout-v2) from other checkout surfaces on the analytics
 * side. Maps to the optional `FrontendEvent.version` field.
 */
const CHECKOUT_UI_VERSION = 'V2';

/**
 * Dual-channel sink for checkout SDK lifecycle events.
 *
 * Publishers still receive `postMessage` via {@link sendFEEvent}; this
 * service additionally POSTs the same lifecycle to events-gateway through
 * `@appcharge/frontend-events-sdk` with `CheckoutAuth`.
 *
 * Auth contract (hard):
 *   - Every network POST includes `Authorization: checkout <sessionToken>`.
 *   - Events fired before a non-empty session token (+ publisher id) are
 *     buffered — never sent unauthenticated.
 *
 * Payload contract:
 *   - Body matches `@appcharge/frontend-events` v1.3+ checkout DTOs
 *     (`CHECKOUT_LIFECYCLE` / `ORDER_AWARDING` / `PAYMENT_INTENT`).
 */
class FrontendEventsService {
  private client: FrontendEventsClient | null = null;
  private clientPromise: Promise<FrontendEventsClient> | null = null;
  private sdkPromise: Promise<FrontendEventsSdk> | null = null;
  private sessionToken: string | undefined;
  private publisherId: string | undefined;
  private queue: QueuedEvent[] = [];
  private baseUrlOverride: string | undefined;
  private prefetchStarted = false;

  initialize(options: FrontendEventsInitOptions): void {
    const sessionToken = normalizeRequired(options.sessionToken);
    if (!sessionToken) {
      logError('frontendEventsService.initialize requires a non-empty sessionToken', undefined, {
        hasPublisherId: Boolean(options.publisherId),
      });
      return;
    }

    this.sessionToken = sessionToken;
    if (options.publisherId) {
      const publisherId = normalizeRequired(options.publisherId);
      if (publisherId) this.publisherId = publisherId;
    }
    if (options.baseUrl) this.baseUrlOverride = options.baseUrl;

    this.client = null;
    this.clientPromise = null;

    this.prefetchSdk();
    this.flushQueue();
  }

  setPublisherId(publisherId: string): void {
    const normalized = normalizeRequired(publisherId);
    if (!normalized || normalized === this.publisherId) return;
    this.publisherId = normalized;
    this.client = null;
    this.clientPromise = null;
    this.prefetchSdk();
    this.flushQueue();
  }

  track(event: ESdkEvents, params: Partial<EventParams>): void {
    if (!this.canSendAuthenticated()) {
      this.enqueue({ event, params });
      return;
    }

    this.scheduleSend(event, params);
  }

  reset(): void {
    this.client = null;
    this.clientPromise = null;
    this.sdkPromise = null;
    this.sessionToken = undefined;
    this.publisherId = undefined;
    this.queue = [];
    this.baseUrlOverride = undefined;
    this.prefetchStarted = false;
  }

  private canSendAuthenticated(): boolean {
    return Boolean(this.sessionToken && this.publisherId);
  }

  private enqueue(item: QueuedEvent): void {
    if (this.queue.length >= MAX_QUEUED_EVENTS) {
      this.queue.shift();
    }
    this.queue.push(item);
  }

  private prefetchSdk(): void {
    if (this.prefetchStarted) return;
    this.prefetchStarted = true;
    deferIdle(() => {
      void this.loadSdk().catch((error) => {
        logError('Failed to prefetch frontend-events-sdk', error);
        this.prefetchStarted = false;
      });
    });
  }

  private loadSdk(): Promise<FrontendEventsSdk> {
    if (!this.sdkPromise) {
      this.sdkPromise = import('@appcharge/frontend-events-sdk');
    }
    return this.sdkPromise;
  }

  private resolveBaseUrl(): string {
    return (
      this.baseUrlOverride ||
      getEnvVariable('REACT_APP_EVENTS_API_URL') ||
      getEnvVariable('REACT_APP_API_BASE_URL', 'https://api.appcharge.com')
    );
  }

  private createKeepaliveFetch(): typeof fetch {
    const fetchImpl = typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined;
    if (!fetchImpl) {
      throw new Error('No fetch implementation available for frontend events');
    }
    return (input: RequestInfo | URL, init?: RequestInit) =>
      fetchImpl(input, { ...init, keepalive: true });
  }

  private async getClient(
    sessionToken: string,
    publisherId: string,
  ): Promise<FrontendEventsClient> {
    if (this.client) return this.client;
    if (this.clientPromise) return this.clientPromise;

    this.clientPromise = (async () => {
      const sdk = await this.loadSdk();
      if (this.sessionToken !== sessionToken || this.publisherId !== publisherId) {
        throw new Error('Frontend events client init aborted');
      }
      const client = new sdk.FrontendEventsClient({
        baseUrl: this.resolveBaseUrl(),
        realm: new sdk.CheckoutAuth(),
        context: {
          publisher_id: publisherId,
          session_id: sessionToken,
        },
        // FrontendEvent requires UA-derived device fields — let the SDK stamp them.
        collectStandardFields: true,
        fetch: this.createKeepaliveFetch(),
      });
      this.client = client;
      return client;
    })();

    try {
      return await this.clientPromise;
    } catch (error) {
      this.clientPromise = null;
      throw error;
    }
  }

  private flushQueue(): void {
    if (!this.canSendAuthenticated() || this.queue.length === 0) return;
    const pending = this.queue.splice(0, this.queue.length);
    for (const item of pending) {
      this.scheduleSend(item.event, item.params);
    }
  }

  private scheduleSend(event: ESdkEvents, params: Partial<EventParams>): void {
    // CLOSE_CHECKOUT fires when the page is unloading. Bypass idle so the
    // POST is at least queued before the tab is gone; `keepalive: true` on
    // the injected fetch lets the browser complete it after teardown.
    if (event === ESdkEvents.CLOSE_CHECKOUT) {
      void this.send(event, params);
      return;
    }
    deferIdle(() => {
      void this.send(event, params);
    });
  }

  private async send(event: ESdkEvents, params: Partial<EventParams>): Promise<void> {
    const sessionToken = this.sessionToken;
    const publisherId = this.publisherId;
    if (!sessionToken || !publisherId) return;

    const dto = toFrontendCheckoutEvent(event, params, publisherId);
    if (!dto) return;

    try {
      const client = await this.getClient(sessionToken, publisherId);
      if (this.sessionToken !== sessionToken || !this.sessionToken) {
        logError(
          'Skipped frontend event send: session token changed or cleared before POST',
          undefined,
          { eventType: dto.event_type, event },
        );
        return;
      }
      const resolved = await this.withResolvedDeviceModel(dto);
      const payload = { ...resolved, version: CHECKOUT_UI_VERSION };
      await client.send(payload as unknown as EventInput<FrontendEvent>, sessionToken);
    } catch (error) {
      logError('Failed to send frontend event', error, {
        eventType: dto.event_type,
        event,
      });
    }
  }

  /**
   * Drops the SDK's `"unknown"` device_model sentinel so the field is omitted
   * from the payload. The enricher spreads caller input last, so stamping
   * `device_model: undefined` here overrides the collected `"unknown"`, and
   * `JSON.stringify` then removes the undefined key from the wire body. A
   * genuinely detected model is left untouched for the SDK to stamp.
   */
  private async withResolvedDeviceModel<T extends object>(
    dto: T,
  ): Promise<T | (T & { device_model: undefined })> {
    const sdk = await this.loadSdk();
    const { device_model: deviceModel } = sdk.collectStandardFields();
    if (deviceModel === UNKNOWN_DEVICE_MODEL) {
      return { ...dto, device_model: undefined };
    }
    return dto;
  }
}

function normalizeRequired(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export const frontendEventsService = new FrontendEventsService();

export { FrontendEventsService };
